<script setup lang="ts">
import { computed } from 'vue';
import {
    BCard, BSkeletonLoader, BIcon,
} from '@/design-system';
import { useTranslation } from '@/composables';
import type { ShipmentMovementType } from '@/types/dashboard';

type PropsType = {
    items: ShipmentMovementType[];
    isLoading?: boolean;
    title?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    isLoading: false,
    title: undefined,
});

const { t } = useTranslation();

const skeletonItems = computed(() => Array.from({ length: 5 }, (_, i) => i));
const isEmpty = computed(() => !props.isLoading && props.items.length === 0);

const TYPE_CONFIG: Record<ShipmentMovementType['type'], { icon: string; color: string }> = {
    in: { icon: 'mdi-arrow-down', color: 'accent-green-dashboard' },
    out: { icon: 'mdi-arrow-up', color: 'accent-orange' },
    transfer: { icon: 'mdi-swap-horizontal', color: 'accent-blue-dashboard' },
};

const formatQuantity = (quantity: number, type: ShipmentMovementType['type']) => {
    if (type === 'in') {
        return `+${quantity}`;
    }
    if (type === 'out') {
        return `-${quantity}`;
    }

    return `${quantity}`;
};
</script>

<template>
    <BCard
        variant="flat"
        class="b-shipment-movements pa-0"
    >
        <template
            v-if="title"
            #title
        >
            <div class="text-body-2 font-weight-bold pa-4 pb-2">
                {{ title }}
            </div>
        </template>

        <div class="b-shipment-movements__content pa-4">
            <BSkeletonLoader
                v-if="isLoading"
                type="list-item"
                height="48"
            >
                <div class="d-flex flex-column">
                    <div
                        v-for="i in skeletonItems"
                        :key="i"
                        class="b-shipment-movements__skeleton-item"
                    />
                </div>
            </BSkeletonLoader>

            <div
                v-else-if="isEmpty"
                class="b-shipment-movements__empty"
            >
                <BIcon
                    icon="mdi-truck-remove-outline"
                    size="32"
                    color="grey-400"
                />
                <span class="b-shipment-movements__empty-text">
                    {{ t('common.noDataAvailable') }}
                </span>
            </div>

            <div
                v-else
                class="b-shipment-movements__list"
            >
                <div
                    v-for="(item, index) in items"
                    :key="item.id"
                    class="b-shipment-movements__item dashboard-animate-slide-in"
                    :class="`dashboard-stagger-${Math.min(index + 1, 5)}`"
                >
                    <div
                        class="b-shipment-movements__badge"
                        :style="{
                            backgroundColor: `rgba(var(--v-theme-${TYPE_CONFIG[item.type].color}), 0.12)`,
                            color: `rgb(var(--v-theme-${TYPE_CONFIG[item.type].color}))`,
                        }"
                    >
                        <VIcon
                            :icon="TYPE_CONFIG[item.type].icon"
                            size="14"
                        />
                    </div>

                    <div class="b-shipment-movements__info">
                        <div class="b-shipment-movements__warehouse">
                            {{ item.warehouseName }}
                            <template v-if="item.destinationName">
                                <VIcon
                                    icon="mdi-arrow-right"
                                    size="12"
                                    class="mx-1"
                                />
                                {{ item.destinationName }}
                            </template>
                        </div>
                        <div class="b-shipment-movements__time">
                            {{ item.timeAgo }}
                        </div>
                    </div>

                    <div
                        class="b-shipment-movements__quantity"
                        :style="{ color: `rgb(var(--v-theme-${TYPE_CONFIG[item.type].color}))` }"
                    >
                        {{ formatQuantity(item.quantity, item.type) }} ks
                    </div>
                </div>
            </div>
        </div>
    </BCard>
</template>

<style scoped lang="scss">
@use '@/design-system/styles/dashboard-animations';

.b-shipment-movements {
    border-radius: 8px;
    border: 1px solid rgb(204, 225, 235);
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
}

.b-shipment-movements__content {
    min-height: 120px;
}

.b-shipment-movements__list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.b-shipment-movements__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    gap: 12px;
}

.b-shipment-movements__empty-text {
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.5);
}

.b-shipment-movements__skeleton-item {
    height: 40px;
    border-radius: 8px;
    background-color: rgb(var(--v-theme-grey-50));
    opacity: 0.1;
    margin-bottom: 6px;

    &:last-child {
        margin-bottom: 0;
    }
}

.b-shipment-movements__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background-color: rgb(var(--v-theme-grey-50));
    border-radius: 8px;
    opacity: 0;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgb(var(--v-theme-grey-100));
    }
}

.b-shipment-movements__badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 6px;
}

.b-shipment-movements__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.b-shipment-movements__warehouse {
    font-size: 13px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
}

.b-shipment-movements__time {
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), 0.5);
}

.b-shipment-movements__quantity {
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
}
</style>
